import { useCallback, useLayoutEffect, useRef } from "react";
import type { CustomContainerComponentProps } from "virtua";

/**
 * Sub-pixel slack for "is the scroller at its end". On fractional `devicePixelRatio` displays a
 * scroller flush against its end rarely measures as exactly flush, so an exact comparison never holds.
 */
const PINNED_TOLERANCE_PX = 1.5;

/**
 * The virtualizer's content element for a bottom-anchored list, which is where the list follows its
 * end.
 *
 * This element's height is the list's total size, and `virtua` re-renders it on every change to that
 * size — a child appended, an older page prepended, a child measured or re-measured as it grows. Its
 * layout effect therefore runs once per content change, before paint and before `virtua`'s own
 * scroll adjustment for the same commit, which makes it the one place where "was the reader at the
 * end *before* this growth" can be answered exactly: the scroller's current `scrollTop` against its
 * `scrollHeight` with the growth subtracted back out. A reading taken anywhere else is either stale
 * (the last scroll event) or already polluted by the growth it is trying to see past.
 *
 * Only growth is acted on. A shrinking scroller clamps its own `scrollTop`, so a reader at the end
 * stays there without help, and one who is not must not be moved.
 */
export const EndAnchoredContent = ({ style, children, ref }: CustomContainerComponentProps) => {
  const elementRef = useRef<HTMLDivElement | null>(null);
  const previousHeightRef = useRef<number | null>(null);

  const attach = useCallback(
    (element: HTMLDivElement | null) => {
      elementRef.current = element;
      if (typeof ref === "function") ref(element);
      else if (ref && typeof ref === "object") ref.current = element;
    },
    [ref]
  );

  useLayoutEffect(() => {
    const element = elementRef.current;
    const scroller = element?.parentElement;
    if (!element || !scroller) return;

    const height = element.getBoundingClientRect().height;
    const previousHeight = previousHeightRef.current;
    previousHeightRef.current = height;

    // A list that has just mounted opens at its end; after that, the end is followed only across a
    // growth the reader was already at the end of.
    const isFirstContent = previousHeight === null;
    const growth = isFirstContent ? 0 : height - previousHeight;
    const wasAtEnd = scroller.scrollTop + scroller.clientHeight >= scroller.scrollHeight - growth - PINNED_TOLERANCE_PX;
    if (!isFirstContent && (growth <= 0 || !wasAtEnd)) return;

    // Deferred past this commit's remaining layout effects because `virtua`'s runs after this one
    // (it is the parent) and writes `scrollTop` as an absolute position computed from the last scroll
    // event it saw — which, when it also measured children above the viewport in this pass, lands
    // short of the end and would overwrite a write made here directly.
    queueMicrotask(() => {
      scroller.scrollTop = scroller.scrollHeight;
    });
  });

  return (
    <div ref={attach} data-slot="paginated-list-content" style={style}>
      {children}
    </div>
  );
};
